import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import oneLineTrim from 'common-tags/lib/oneLineTrim';
import FileSaver from 'file-saver';

import FieldCheckbox from '../../basics/form/FieldCheckbox';
import FormSection from '../form/FormSection';
import FormHeader from '../form/FormHeader';
import Copy from '../../basics/text/TextCopy';
import Link from '../../basics/text/TextLink';
import DataList from '../../basics/text/TextDataList';
import * as money from '../../../helpers/money';
import {
  FORM_FIELD_PAYMENT_TYPE_DEBIT,
  FORM_FIELD_PAYMENT_TYPE_ADVANCE,
  FORM_FIELD_PAYMENT_TYPE_CC,
} from '../../../helpers/constants';
import suitcss from '../../../helpers/suitcss';
import { getAppEntityName } from '../../../helpers/entity';
import { preventDefault } from '../../../helpers/functional';
import CreditCardInfo from '../../basics/form/CreditCardInfo';
import * as paymentStep from '../../../containers/activation/formConfigActivationPayment';
import * as personalStep from '../../../containers/activation/formConfigActivationPersonal';
import * as topupStep from '../../../containers/form/steps/formConfigTopup';
import { getConsentTextById } from '../../../helpers/misc';
import { send } from '../../../actions/request/send';
import EECCActivationDocumentRequest from '../../../model/requests/EECCActivationDocumentRequest';


class ActivationFormConfirm extends PureComponent {
  constructor(...args) {
    super(...args);
    this.manageDownloadEECCPdf = this.manageDownloadEECCPdf.bind(this);
  }

  getPaymentContent() {
    const { ui, data } = this.props;
    switch (data.paymentType) {
      case FORM_FIELD_PAYMENT_TYPE_DEBIT:
        return [
          <li key="1">{ui.guiPaymentMethodDirectDebit}</li>,
          <li key="2">{ui.guiSymbolIban} {data.iban}</li>,
        ];
      case FORM_FIELD_PAYMENT_TYPE_CC:
        return [
          <li key="1">{ui.guiPaymentMethodCreditCard}</li>,
          <li key="2">
            {this.props.creditCardData &&
            <CreditCardInfo data={this.props.creditCardData} />
            }
          </li>,
        ];
      case FORM_FIELD_PAYMENT_TYPE_ADVANCE:
        return [
          <li key="1">{ui.guiPaymentMethodAdvance}</li>,
          <li key="2">{ui.guiPaymentAdvanceInfo}</li>,
        ];
      default:
        return null;
    }
  }

  getTopupMsg() {
    const {
      ui,
      data: {
        autoTopupMonthlyType,
        topupType,
        topupAmount,
        autoTopupTrigger,
        autoTopupType,
      },
    } = this.props;

    switch (topupType) {
      case 'auto':
        switch (autoTopupType) {
          case 'balance':
            return ui.guiTopupBalanceThresholdInfo
              .replace('{AMOUNT}', money.toFullCurrency({ unit: topupAmount, currency: 'EUR' }))
              .replace('{THRESHOLD}', money.toFullCurrency({ unit: autoTopupTrigger, currency: 'EUR' }));
          case 'monthly':
            switch (autoTopupMonthlyType) {
              case 'B':
                return ui.guiTopupMonthly2NdInfo
                  .replace('{AMOUNT}', money.toFullCurrency({ unit: topupAmount, currency: 'EUR' }));
              case 'D':
                return ui.guiTopupMonthly19ThInfo
                  .replace('{AMOUNT}', money.toFullCurrency({ unit: topupAmount, currency: 'EUR' }));
              default:
                // Should never happen
                return '';
            }

          default:
            return autoTopupType;
        }
      case 'single':
        return ui.guiTopupSingleInfo
          .replace('{AMOUNT}', money.toFullCurrency({ unit: topupAmount, currency: 'EUR' }));
      case 'none':
        return ui.guiTopupNone;
      default:
        // Should never happen
        return '';
    }
  }

  async manageDownloadEECCPdf(ev) {
    if (ev.target.tagName !== 'BUTTON' && ev.target.parentNode.tagName !== 'BUTTON') {
      return;
    }
    ev.preventDefault();
    const format = 'pdf';
    const pdfData = await this.props.dispatch(
      send(
        new EECCActivationDocumentRequest(
          this.props.formValues.msisdn, this.props.processId, { format }),
      ));

    if (pdfData.status === 200 && process.browser) {
      const blob = new window.Blob([pdfData.body.blob], { type: `application/${format};` }); //eslint-disable-line
      const fileName = 'Vertragszusammenfassung';
      FileSaver.saveAs(blob, `${fileName}.${format}`);
    }
  }

  renderLink(stepId) {
    const { ui, onStepClick } = this.props;
    return (
      <div
        className={suitcss({
          descendantName: 'link',
        }, this)}
      >
        <Link
          element="button"
          onClick={preventDefault(() => onStepClick(stepId))}
        >
          {ui.guiWordEdit}
        </Link>
      </div>
    );
  }

  renderConfirm() {
    const { ui, data } = this.props;
    const paymentContent = this.getPaymentContent();
    const topupInfo = this.getTopupMsg();
    const { tariff, creditBalance } = data.simStatusResponse;

    const renderValue = (comp, link) => (
      <div className={suitcss({ descendantName: 'summaryListValue' }, this)}>
        <div className={suitcss({ descendantName: 'summaryDataListView' }, this)}>
          {comp}
        </div>
        <div className={suitcss({ descendantName: 'summaryDataListButton' }, this)}>
          {link}
        </div>
      </div>
    );
    const personalData = (
      <ul>
        <li>{data.salutations} {data.firstName} {data.lastName}</li>
        <li>{data.street} {data.houseNumber}</li>
        <li>{data.zip} {data.city}</li>
      </ul>
    );
    const getTariffData = () => (
      <ul>
        <li>{getAppEntityName(tariff)}</li>
        {creditBalance && <li>{`${ui.guiWordInitialBalance} ${money.toFullCurrency({ unit: creditBalance.unit, currency: 'EUR' })}`}</li>}
      </ul>
    );
    const items = [
      tariff && {
        key: ui.guiWordTariff,
        value: renderValue(getTariffData(), null),
      },
      {
        key: ui.guiWordTopup,
        value: renderValue(<Copy>{topupInfo}</Copy>, this.renderLink(topupStep.id)),
      },
      data.topupType !== 'none' && {
        key: ui.guiWordPaymentData,
        value: renderValue(<ul>{paymentContent}</ul>, this.renderLink(paymentStep.id)),
      },
      {
        key: ui.guiWordPersonalData,
        value: renderValue(personalData, this.renderLink(personalStep.id)),
      },
    ].filter(e => e);

    return (
      <div
        className={suitcss({
          descendantName: 'summeryContainer',
          utilities: ['lCol8'],
        }, this)}
        data-mf-replace=""
      >
        <DataList withoutEllipsis items={items} />
      </div>
    );
  }

  renderConsulting() {
    const { fieldMap } = this.props;
    return (
      <div className={suitcss({ utilities: ['grid'] })}>
        <Field
          {...fieldMap.adConsent}
          component={FieldCheckbox}
          raw
          utilities={['col12', 'mlCol8']}
        />
        <Field
          {...fieldMap.passData}
          component={FieldCheckbox}
          raw
          utilities={['col12', 'mlCol8']}
        />
      </div>
    );
  }

  renderAgbAndRevocation() {
    const { consentTexts, ui, params } = this.props;
    const { termsAgbHint, termsDataProtection } = params;
    const termsAgbHintPdfLink = termsAgbHint && termsAgbHint.replace('{CONTRACT_SUMMARY_PDF}', oneLineTrim`
      <button class="TextLink">
        ${ui.guiContractSummary}
      </button>
    `);

    return (
      <Copy
        element="div"
        className={suitcss({
          descendantName: 'list',
          utilities: ['mlCol8'],
        }, this)}
      >
        <ul>
          <Copy
            raw
            element="li"
            size="small"
            utilities={['paddingLeft']}
            onClick={this.manageDownloadEECCPdf}
          >
            {termsAgbHintPdfLink}
          </Copy>
          <Copy
            raw
            element="li"
            size="small"
            utilities={['paddingLeft']}
          >
            {termsDataProtection}
          </Copy>
        </ul>
        <Copy raw size="small">
          {getConsentTextById(consentTexts, 'solvencycheck_terms')}
        </Copy>
      </Copy>
    );
  }

  render() {
    const { params } = this.props;
    const {
      confirmHeadline,
      consultingHeadline,
      termsHeadline,
    } = params;

    return (
      <FormSection className={suitcss({ modifiers: ['bewStyling'] }, this)} asGrid>
        <FormHeader headline={confirmHeadline} />
        {this.renderConfirm()}
        <FormHeader headline={termsHeadline} />
        {this.renderAgbAndRevocation()}
        <FormHeader headline={consultingHeadline} />
        {this.renderConsulting()}
      </FormSection>
    );
  }
}

ActivationFormConfirm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  onStepClick: PropTypes.func.isRequired,
  fieldMap: PropTypes.object.isRequired,
  params: PropTypes.shape({
    confirmHeadline: PropTypes.string.isRequired,
    consultingHeadline: PropTypes.string.isRequired,
    consultingCopy: PropTypes.string.isRequired,
    termsHeadline: PropTypes.string.isRequired,
    termsAgbHint: PropTypes.string.isRequired,
    termsContractSummary: PropTypes.string,
    termsDataProtection: PropTypes.string.isRequired,
  }),
  creditCardData: PropTypes.object,
  data: PropTypes.object.isRequired,
  ui: PropTypes.shape({
    guiTopupSingleInfo: PropTypes.string.isRequired,
    guiTopupBalanceThresholdInfo: PropTypes.string.isRequired,
    guiWordMore: PropTypes.string.isRequired,
    guiWordLess: PropTypes.string.isRequired,
    guiPaymentAdvanceInfo: PropTypes.string.isRequired,
    guiSymbolIban: PropTypes.string.isRequired,
    guiWordEdit: PropTypes.string.isRequired,
    guiContractSummary: PropTypes.string,
  }),
  consentTexts: PropTypes.array,
  processId: PropTypes.string,
};

export default ActivationFormConfirm;
